import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';
import {
    buildExitCandidateOptions,
    normalizeGlobalExitCandidateSearchQuery,
    type ExitCandidateSource,
    type GlobalExitCandidateSearchResult
} from './exitCandidateSelectItems';

export { toExitCandidateSearchItem } from './exitCandidateSelectItems';
export type { ExitCandidateSource, GlobalExitCandidateSearchResult } from './exitCandidateSelectItems';

const SEARCH_LIMIT = 50;
const INCLUDE_IDS_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 250;
const HYDRATION_QUERY = '__project314_selected_exit_candidate_hydration__';

const missingItem = (candidateId: number): SearchSelectItem => ({
    label: `Exit candidate #${candidateId}`,
    meta: 'Loading candidate details…',
    name: `Exit candidate #${candidateId}`,
    value: candidateId
});

const chunkIds = (ids: number[]): number[][] => {
    const chunks: number[][] = [];

    for (let index = 0; index < ids.length; index += INCLUDE_IDS_LIMIT) {
        chunks.push(ids.slice(index, index + INCLUDE_IDS_LIMIT));
    }

    return chunks;
};

interface ExitCandidateSelectCommonProps extends SearchSelectFormProps {
    source?: ExitCandidateSource;
    status?: 'active' | 'invalid';
    /** Groups candidates with the same executable behavior into one search result. */
    collapseEquivalent?: boolean;
    /** Prefers this strategy's attached candidate when selecting a representative from an equivalent group. */
    preferredStrategyId?: number;
    noOptionsMessage?: string;
    /**
     * Restricts the result list to an already-defined candidate set, such as a strategy's attachments. The list is
     * the complete option set: it is hydrated by ID and filtered locally, so the global search page never decides
     * which of the allowed candidates are selectable.
     */
    allowedIds?: number[];
    /** Exact candidate DTOs already loaded by the owning surface, such as strategy attachments. */
    hydratedCandidates?: GlobalExitCandidateSearchResult[];
}

interface ExitCandidateSelectSingleProps extends ExitCandidateSelectCommonProps {
    mode: 'single';
    value: number | null;
    onChange: (candidateId: number | null) => void;
}

interface ExitCandidateSelectMultiProps extends ExitCandidateSelectCommonProps {
    mode: 'multi';
    value: number[];
    onChange: (candidateIds: number[]) => void;
}

export type ExitCandidateSelectProps = ExitCandidateSelectSingleProps | ExitCandidateSelectMultiProps;

/**
 * Server-backed global exit-candidate typeahead. Equivalent behavior may be collapsed for a quieter catalog, and
 * every selected ID is independently hydrated so saved candidates remain readable outside the current result page.
 */
const ExitCandidateSelect = (props: ExitCandidateSelectProps) => {
    const {
        source,
        status = 'active',
        collapseEquivalent,
        preferredStrategyId,
        allowedIds,
        hydratedCandidates = [],
        noOptionsMessage,
        mode,
        value,
        onChange,
        ...formProps
    } = props;
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    useEffect(() => {
        const timeout = window.setTimeout(
            () => setDebouncedSearchText(normalizeGlobalExitCandidateSearchQuery(searchText)),
            SEARCH_DEBOUNCE_MS
        );
        return () => window.clearTimeout(timeout);
    }, [searchText]);

    const selectedIds = useMemo(() => (mode === 'multi' ? value : value === null ? [] : [value]), [mode, value]);
    const hydrationIds = useMemo(() => Array.from(new Set([...(allowedIds ?? []), ...selectedIds])), [allowedIds, selectedIds]);
    const selectedIdChunks = useMemo(() => chunkIds(hydrationIds), [hydrationIds]);

    // An allow-list is already the full option set, so the global search only runs for open-ended browsing.
    const isBoundedToAllowedIds = allowedIds !== undefined;
    const searchQuery = useQuery({
        ...trpc.tradeLogicExitCandidates.searchGlobalCandidates.queryOptions({
            ...(debouncedSearchText ? { query: debouncedSearchText } : {}),
            ...(source ? { source } : {}),
            ...(collapseEquivalent ? { collapseEquivalent: true } : {}),
            ...(preferredStrategyId ? { preferredStrategyId } : {}),
            status,
            limit: SEARCH_LIMIT
        }),
        enabled: !isBoundedToAllowedIds,
        staleTime: 30000
    });

    const hydrationQueries = useQueries({
        queries: selectedIdChunks.map((includeIds) => ({
            // Keep hydration uncollapsed so a saved historical candidate ID remains readable.
            ...trpc.tradeLogicExitCandidates.searchGlobalCandidates.queryOptions({
                query: HYDRATION_QUERY,
                status,
                includeIds,
                limit: 1
            }),
            staleTime: 60000
        }))
    });

    const hydrationCandidates = useMemo(() => {
        const candidates: GlobalExitCandidateSearchResult[] = [];

        for (const hydrationQuery of hydrationQueries) {
            for (const candidate of hydrationQuery.data?.candidates ?? []) candidates.push(candidate);
        }

        return candidates;
    }, [hydrationQueries]);
    const { items, isServerFiltered } = useMemo(
        () =>
            buildExitCandidateOptions({
                allowedIds,
                hydratedCandidates,
                searchCandidates: searchQuery.data?.candidates ?? [],
                hydrationCandidates
            }),
        [allowedIds, hydratedCandidates, hydrationCandidates, searchQuery.data?.candidates]
    );
    const hydrationError = hydrationQueries.find((query) => query.error instanceof Error)?.error;
    const loadError =
        searchQuery.error instanceof Error
            ? `Failed to search exit candidates: ${searchQuery.error.message}`
            : hydrationError instanceof Error
              ? `Failed to load the selected exit candidate: ${hydrationError.message}`
              : undefined;

    const commonProps = {
        ...formProps,
        isLoading: searchQuery.isLoading || hydrationQueries.some((query) => query.isLoading),
        isServerFiltered,
        items,
        loadError,
        noOptionsMessage:
            noOptionsMessage ?? (debouncedSearchText ? 'No global exit candidates match this search' : 'No active global exit candidates'),
        onSearchChange: setSearchText,
        resolveMissingItem: missingItem
    };

    return mode === 'multi' ? (
        <SearchSelect {...commonProps} mode="multi" value={value} onChange={onChange} />
    ) : (
        <SearchSelect {...commonProps} mode="single" value={value} onChange={onChange} />
    );
};

export default ExitCandidateSelect;
